import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { createChart, LineStyle } from 'lightweight-charts';

const formatTickLabel = (time) => {
  if (time == null) {
    return '';
  }

  let seconds = null;

  if (typeof time === 'number') {
    seconds = time;
  } else if (typeof time === 'string') {
    const parsed = Number(time);
    if (!Number.isNaN(parsed)) {
      seconds = parsed;
    }
  } else if (typeof time === 'object') {
    if (typeof time.seconds === 'number') {
      seconds = time.seconds;
    } else if (typeof time.timestamp === 'number') {
      seconds = time.timestamp;
    } else if (typeof time.year === 'number') {
      const date = new Date(Date.UTC(time.year, time.month - 1, time.day));
      return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
    }
  }

  if (seconds == null || Number.isNaN(seconds)) {
    return '';
  }

  const date = new Date(seconds * 1000);
  const dayPart = date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
  });
  const timePart = date.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${dayPart} ${timePart}`;
};

const StableChart = ({ data, trades, chartType = 'index', showSupertrend = false }) => {
  const chartContainerRef = useRef();
  const [visibleRange, setVisibleRange] = useState(null);

  const handleVisibleRangeChange = useCallback((range) => {
    console.log('🔄 VISIBLE RANGE CHANGE EVENT:', range);
    if (range && range.from != null && range.to != null) {
      console.log('🔄 Setting visible range:', { from: range.from, to: range.to });
      setVisibleRange({ from: range.from, to: range.to });
    } else {
      console.log('🔄 Invalid range, not setting visibleRange');
    }
  }, []);

  useEffect(() => {
    console.log('StableChart render with data type:', chartType, data?.length, 'items');
    console.log('StableChart trades:', trades?.length);
    console.log('First few data items:', data?.slice(0, 3));
  }, [data, chartType, trades]);

  // Debug logging for component props
  useEffect(() => {
    console.log('StableChart props:', {
      chartType,
      showSupertrend,
      'data.length': data ? data.length : 0,
      'trades.length': trades ? trades.length : 0
    });
    // Log browser timezone for debugging
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      console.log('Browser time zone:', tz);
    } catch {}
    
    if (chartType === 'premium') {
      console.log('StableChart - Premium chart requested');
      console.log('Premium data sample:', data?.slice(0, 3));
      
      // Check premium data structure
      if (data && data.length > 0) {
        console.log('Premium data first item structure:', {
          keys: Object.keys(data[0]),
          timestamp: data[0].timestamp,
          combined_premium: data[0].combined_premium,
          supertrend: data[0].supertrend,
          supertrend_direction: data[0].supertrend_direction
        });
      } else {
        console.error('Premium data is empty or undefined');
      }
    }
    if (!data || !data.length || !chartContainerRef.current) {
      console.log("No data or container ref available");
      return;
    }

    console.log(`StableChart - Rendering ${chartType} chart with v3.8.0`);
    console.log("Trades sample:", trades?.slice(0, 2));
    console.log("Chart type:", chartType, "Show supertrend:", showSupertrend);

    chartContainerRef.current.innerHTML = '';
    
    // Create chart with v3.8.0 API
    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: chartContainerRef.current.clientHeight || 500,
      layout: {
        background: { type: 'solid', color: '#ffffff' },
        textColor: '#111827',
      },
      timeScale: {
        tickMarkFormatter: formatTickLabel,
        borderColor: '#9aa0a6',
        visible: true,
        timeVisible: true,
        secondsVisible: false,
        lockVisibleTimeRangeOnResize: true,
        rightBarStaysOnScroll: true,
        fixLeftEdge: false,
        fixRightEdge: false,
        borderVisible: true,
        rightOffset: 12,
        barSpacing: 10,
        minBarSpacing: 6,
      },
      leftPriceScale: {
        borderColor: '#d1d4dc',
        visible: true,
      },
      crosshair: {
        vertLine: {
          width: 1,
          color: '#2962FF',
          style: 0,
        },
        horzLine: {
          width: 1,
          color: '#2962FF',
          style: 0,
        },
      },
      // Use default localization for time axis labels
    });

    chart.timeScale().subscribeVisibleTimeRangeChange(handleVisibleRangeChange);

    try {
      // Create a small overlay to show crosshair time clearly
      const overlayId = 'chart-time-overlay';
      let overlayEl = document.getElementById(overlayId);
      if (!overlayEl) {
        overlayEl = document.createElement('div');
        overlayEl.id = overlayId;
        overlayEl.style.position = 'absolute';
        overlayEl.style.right = '8px';
        overlayEl.style.top = '-20px';
        overlayEl.style.fontSize = '12px';
        overlayEl.style.color = '#111827';
        overlayEl.style.background = 'rgba(255,255,255,0.8)';
        overlayEl.style.padding = '2px 6px';
        overlayEl.style.borderRadius = '4px';
        overlayEl.style.pointerEvents = 'none';
        if (chartContainerRef.current && chartContainerRef.current.parentElement) {
          chartContainerRef.current.parentElement.style.position = 'relative';
          chartContainerRef.current.parentElement.insertBefore(overlayEl, chartContainerRef.current);
        }
      }

      const valuesOverlayId = 'chart-values-overlay';
      let valuesOverlayEl = document.getElementById(valuesOverlayId);
      if (!valuesOverlayEl) {
        valuesOverlayEl = document.createElement('div');
        valuesOverlayEl.id = valuesOverlayId;
        valuesOverlayEl.style.position = 'absolute';
        valuesOverlayEl.style.left = '8px';
        valuesOverlayEl.style.top = '-20px';
        valuesOverlayEl.style.fontSize = '12px';
        valuesOverlayEl.style.color = '#111827';
        valuesOverlayEl.style.background = 'rgba(255,255,255,0.8)';
        valuesOverlayEl.style.padding = '2px 6px';
        valuesOverlayEl.style.borderRadius = '4px';
        valuesOverlayEl.style.pointerEvents = 'none';
        valuesOverlayEl.textContent = '';
        if (chartContainerRef.current && chartContainerRef.current.parentElement) {
          chartContainerRef.current.parentElement.style.position = 'relative';
          chartContainerRef.current.parentElement.insertBefore(valuesOverlayEl, chartContainerRef.current);
        }
      }
      // Format data for chart - using v3.8.0 format which uses UTCTimestamp
      // Filter data to market hours (9:15 AM - 3:30 PM IST) as extra safety
      console.log('📊 MARKET HOURS FILTER:');
      const originalCount = data.length;
      
      const marketHoursData = data.filter(item => {
        if (!item.timestamp) return false;
        
        // Parse time from timestamp
        const timestamp = new Date(item.timestamp);
        const hour = timestamp.getHours();
        const minute = timestamp.getMinutes();
        
        // Market hours: 9:15 AM to 3:29 PM IST (market closes AT 3:30)
        const isMarketHours = 
          (hour === 9 && minute >= 15) ||  // 9:15 AM onwards
          (hour >= 10 && hour <= 14) ||    // 10 AM to 2:59 PM
          (hour === 15 && minute < 30);    // Up to 3:29 PM (NOT including 3:30)
        
        return isMarketHours;
      });
      
      const filteredCount = originalCount - marketHoursData.length;
      console.log(`Original: ${originalCount}, Market hours: ${marketHoursData.length}, Filtered: ${filteredCount}`);
      
      if (marketHoursData.length > 0) {
        const first = marketHoursData[0].timestamp;
        const last = marketHoursData[marketHoursData.length - 1].timestamp;
        console.log(`Chart time range: ${first} to ${last}`);
      }
      
      // Use filtered data for chart
      const chartData = marketHoursData;
      
      const formattedData = chartData.map(item => {
        let timestamp;
        
        if (item.timestamp) {
          // Handle timestamp field - backend provides IST timestamps without TZ
          // Parse explicitly as IST by appending '+05:30' to stabilize across browsers
          
          // Check if timestamp exists and has expected format
          if (item.timestamp && typeof item.timestamp === 'string' && 
              item.timestamp.includes('T') && !item.timestamp.includes('+') && !item.timestamp.includes('Z')) {
            
            console.log(`🔧 PROCESSING TIMESTAMP: "${item.timestamp}"`);
            
            try {
              // Parse as IST explicitly
              const istString = `${item.timestamp}+05:30`;
              const istDate = new Date(istString);
              timestamp = Math.floor(istDate.getTime() / 1000);
              
              console.log(`🔧 PRICE DATA (IST): ${item.timestamp} -> ${istDate.toLocaleString()} (epoch ${timestamp})`);
              
            } catch (error) {
              console.error(`❌ Error parsing timestamp "${item.timestamp}":`, error);
              // Fallback to simple parsing
              timestamp = Math.floor(new Date(item.timestamp).getTime() / 1000);
            }
          } else {
            // Has timezone info or other format
            const date = new Date(item.timestamp);
            timestamp = Math.floor(date.getTime() / 1000);
          }
        } else if (item.time) {
          if (typeof item.time === 'string') {
            // Handle ISO format string
            const date = new Date(item.time);
            timestamp = Math.floor(date.getTime() / 1000);
          } else if (typeof item.time === 'number') {
            timestamp = item.time;
          }
        } else if (item.date) {
          // Handle date field
          const date = new Date(item.date);
          timestamp = Math.floor(date.getTime() / 1000);
        } else {
          // Fallback if no timestamp - should not happen
          timestamp = Math.floor(new Date().getTime() / 1000) - (data.length - data.indexOf(item)) * 86400;
        }
        
        return {
          time: timestamp,
          open: parseFloat(item.open) || 0,
          high: parseFloat(item.high) || 0,
          low: parseFloat(item.low) || 0,
          close: parseFloat(item.close) || 0,
          volume: parseFloat(item.volume) || 0,
          oi: item.oi !== undefined && item.oi !== null ? (parseFloat(item.oi) || null) : null,
          vwap: item.vwap !== undefined && item.vwap !== null ? (parseFloat(item.vwap) || null) : null,
          lr: item.lr !== undefined && item.lr !== null ? (parseFloat(item.lr) || null) : null,
          sl_line: item.sl_line !== undefined && item.sl_line !== null ? (parseFloat(item.sl_line) || null) : null,
          down_line: item.down_line !== undefined && item.down_line !== null ? (parseFloat(item.down_line) || null) : null,
          sd: item.sd !== undefined && item.sd !== null ? (parseFloat(item.sd) || null) : null,
          mode: item.mode !== undefined && item.mode !== null ? parseInt(item.mode, 10) : null,
          prev_high: item.prev_high !== undefined && item.prev_high !== null ? (parseFloat(item.prev_high) || null) : null,
        };
      });
      
      // Debug formatted timestamps  
      console.log('🎯 FINAL TIMESTAMPS FOR CHART:');
      formattedData.slice(0, 3).forEach((item, index) => {
        const chartDate = new Date(item.time * 1000);
        console.log(`Chart Point ${index + 1}:`);
        console.log(`  Unix timestamp: ${item.time}`);
        console.log(`  Chart will show: ${chartDate.toLocaleString()} (${chartDate.getHours()}:${chartDate.getMinutes()})`);
        console.log(`  ✅ Should be: 9:15 AM IST, 9:30 AM IST, etc.`);
      });
      
      console.log("Current chart type:", chartType);
      console.log("Show supertrend:", showSupertrend);
      
      // References to keep track of series for marker placement
      let activeSeries = null;
      let candlestickSeries = null;
      let vwapSeries = null;
      let lrSeries = null;
      let slSeries = null;
      let downLineSeries = null;
      let prevHighSeries = null;

      const metaByTime = new Map();
      const sortedMetaData = [...formattedData].sort((a, b) => a.time - b.time);
      let prevSlLine = null;
      let prevVwap = null;
      let prevDownLine = null;
      sortedMetaData.forEach(pt => {
        const slCalcCur = Math.max(
          pt.vwap != null ? pt.vwap : -Infinity,
          pt.down_line != null ? pt.down_line : -Infinity,
          prevSlLine != null ? prevSlLine : -Infinity,
        );
        const slCalcPrev = Math.max(
          prevVwap != null ? prevVwap : -Infinity,
          prevDownLine != null ? prevDownLine : -Infinity,
          prevSlLine != null ? prevSlLine : -Infinity,
        );
        metaByTime.set(pt.time, {
          open: pt.open,
          high: pt.high,
          low: pt.low,
          close: pt.close,
          volume: pt.volume,
          oi: pt.oi,
          vwap: pt.vwap,
          lr: pt.lr,
          sl_line: pt.sl_line,
          prev_sl_line: prevSlLine,
          prev_vwap: prevVwap,
          prev_down_line: prevDownLine,
          sl_calc_cur: (Number.isFinite(slCalcCur) ? slCalcCur : null),
          sl_calc_prev: (Number.isFinite(slCalcPrev) ? slCalcPrev : null),
          down_line: pt.down_line,
          sd: pt.sd,
          mode: pt.mode,
          prev_high: pt.prev_high,
        });
        prevSlLine = pt.sl_line;
        prevVwap = pt.vwap;
        prevDownLine = pt.down_line;
      });
      
      // Function to render premium chart (no need for useCallback here since it's only called inside useEffect)
      const renderPremiumChart = (chart) => {
        if (!data || data.length === 0) {
          console.log('No premium data available');
          return;
        }
        
        console.log('Rendering premium chart with data:', data.slice(0, 3));
        console.log('Premium data first item keys:', data[0] ? Object.keys(data[0]) : 'No data');
        
        // Create a line series for combined premium
        const lineSeries = chart.addLineSeries({
          color: 'rgba(4, 111, 232, 1)',
          lineWidth: 2,
          title: 'Combined Premium',
        });
        
        // Map premium data to the required format
        const premiumData = data
          .filter(item => item.combined_premium !== undefined && item.combined_premium !== null)
          .map(item => ({
            time: Math.floor(new Date(item.timestamp).getTime() / 1000),
            value: item.combined_premium
          }))
          .sort((a, b) => a.time - b.time);
        
        console.log('Mapped premium data points:', premiumData.length);
        if (premiumData.length > 0) {
          console.log('Premium data sample:', premiumData.slice(0, 3));
        }
        
        lineSeries.setData(premiumData);
        // Nudge time scale to ensure labels render
        chart.applyOptions({ timeScale: { visible: true, rightOffset: 12, barSpacing: 10, secondsVisible: false } });
        setTimeout(() => { try { chart.timeScale().fitContent(); } catch {} }, 0);
        requestAnimationFrame(() => { try { chart.timeScale().fitContent(); } catch {} });
        if (premiumData.length > 1) {
          const from = premiumData[0].time;
          const to = premiumData[premiumData.length - 1].time;
          console.log('Forcing visibleRange (premium):', { from, to });
          try { chart.timeScale().setVisibleRange({ from, to }); } catch {}
          try { chart.applyOptions({ timeScale: { visible: true } }); } catch {}
          const range = chart.timeScale().getVisibleRange();
          console.log('Visible range after premium set:', range);
          setVisibleRange({ from, to });
        }
        chart.timeScale().applyOptions({ tickMarkFormatter: formatTickLabel });
        
        // Add supertrend if available and requested
        if (showSupertrend) {
          const supertrendData = data
            .filter(item => item.supertrend !== undefined && item.supertrend !== null)
            .map(item => ({
              time: Math.floor(new Date(item.timestamp).getTime() / 1000),
              value: item.supertrend
            }));
            
          console.log('Supertrend data points:', supertrendData.length);
          
          if (supertrendData.length > 0) {
            const supertrendSeries = chart.addLineSeries({
              color: 'rgba(255, 144, 0, 1)',
              lineWidth: 2,
              title: 'Supertrend',
            });
            supertrendSeries.setData(supertrendData);
          } else {
            console.warn('No supertrend data available');
          }
        }
        
        // Add buy/sell markers
        if (trades && trades.length > 0) {
          // Filter for only index trades
          const indexTrades = trades.filter(trade => 
            trade.instrument_type === 'INDEX' || 
            (!trade.option_type && trade.symbol && trade.symbol.includes('NIFTY'))
          );
          
          console.log('Filtered index trades for markers:', indexTrades.length);
          
          // Fallback: if no index trades are found, use all trades
          const tradesForMarkers = indexTrades.length > 0 ? indexTrades : trades;
          
          // Create a map of timestamps to consolidate multiple trades at the same time
          const tradeMap = {};
          tradesForMarkers.forEach(trade => {
            const timestamp = trade.timestamp;
            if (!tradeMap[timestamp]) {
              tradeMap[timestamp] = [];
            }
            tradeMap[timestamp].push(trade);
          });
          
          // Find matching data points and add markers
          Object.keys(tradeMap).forEach(timestamp => {
            // Convert to Unix timestamp in seconds
            const tradeTime = Math.floor(new Date(timestamp).getTime() / 1000);
            
            // Find a matching data point
            const matchingDataPoint = premiumData.find(item => {
              // Compare Unix timestamps
              return Math.abs(item.time - tradeTime) < 300; // within 5 minutes
            });
            
            if (matchingDataPoint) {
              const trades = tradeMap[timestamp];
              const buyCount = trades.filter(t => t.direction === 'BUY').length;
              const sellCount = trades.filter(t => t.direction === 'SELL').length;
              
              // Add marker for buy
              if (buyCount > 0) {
                lineSeries.createPriceLine({
                  price: matchingDataPoint.value,
                  color: '#2196F3',
                  lineWidth: 2,
                  lineStyle: LineStyle.Solid,
                  axisLabelVisible: true,
                  title: `BUY (${buyCount})`,
                });
              }
              
              // Add marker for sell
              if (sellCount > 0) {
                lineSeries.createPriceLine({
                  price: matchingDataPoint.value,
                  color: '#FF5252',
                  lineWidth: 2,
                  lineStyle: LineStyle.Solid,
                  axisLabelVisible: true,
                  title: `SELL (${sellCount})`,
                });
              }
            }
          });
        }
        
        return lineSeries;
      };

      if (chartType === 'premium') {
        const premiumSeries = renderPremiumChart(chart);
        activeSeries = premiumSeries;
        
        // Fit chart content after rendering premium chart
        chart.timeScale().fitContent();
      } else {
        // For index price chart, use candlestick series
        candlestickSeries = chart.addCandlestickSeries({
          upColor: '#26a69a',
          downColor: '#ef5350',
          borderVisible: false,
          wickUpColor: '#26a69a',
          wickDownColor: '#ef5350',
        });
        
        // Set as the active series for markers
        activeSeries = candlestickSeries;
        
        // IMPORTANT: Reuse the earlier computed formattedData (with unified timestamp parsing)
        // to avoid any time drift between candles and markers.
        const validCandleData = formattedData.filter(candle =>
          candle.open !== undefined &&
          candle.high !== undefined &&
          candle.low !== undefined &&
          candle.close !== undefined
        );
        // Ensure ascending time order for lightweight-charts
        const sortedCandleData = [...validCandleData].sort((a, b) => a.time - b.time);
        candlestickSeries.setData(sortedCandleData);

        // Plot VWAP / LR / SL (SL in red)
        const vwapData = formattedData
          .filter(p => p.vwap !== null && p.vwap !== undefined)
          .map(p => ({ time: p.time, value: p.vwap }))
          .sort((a, b) => a.time - b.time);

        const lrData = formattedData
          .filter(p => p.lr !== null && p.lr !== undefined)
          .map(p => ({ time: p.time, value: p.lr }))
          .sort((a, b) => a.time - b.time);

        const slData = formattedData
          .filter(p => p.sl_line !== null && p.sl_line !== undefined)
          .map(p => ({ time: p.time, value: p.sl_line }))
          .sort((a, b) => a.time - b.time);

        const downLineData = formattedData
          .filter(p => p.down_line !== null && p.down_line !== undefined)
          .map(p => ({ time: p.time, value: p.down_line }))
          .sort((a, b) => a.time - b.time);

        const prevHighData = formattedData
          .filter(p => p.prev_high !== null && p.prev_high !== undefined)
          .map(p => ({ time: p.time, value: p.prev_high }))
          .sort((a, b) => a.time - b.time);

        if (vwapData.length > 0) {
          vwapSeries = chart.addLineSeries({
            color: '#2563eb',
            lineWidth: 2,
            title: 'VWAP',
          });
          vwapSeries.setData(vwapData);
        }

        if (lrData.length > 0) {
          lrSeries = chart.addLineSeries({
            color: '#7c3aed',
            lineWidth: 2,
            title: 'LR',
          });
          lrSeries.setData(lrData);
        }

        if (slData.length > 0) {
          slSeries = chart.addLineSeries({
            color: '#ef4444',
            lineWidth: 2,
            title: 'SL',
          });
          slSeries.setData(slData);
        }

        if (downLineData.length > 0) {
          downLineSeries = chart.addLineSeries({
            color: '#10b981',
            lineWidth: 2,
            title: 'DownLine',
          });
          downLineSeries.setData(downLineData);
        }

        if (prevHighData.length > 0) {
          prevHighSeries = chart.addLineSeries({
            color: '#f59e0b',
            lineWidth: 2,
            title: 'Prev16High',
          });
          prevHighSeries.setData(prevHighData);
        }
        // Nudge time scale to ensure labels render
        chart.applyOptions({ timeScale: { visible: true, rightOffset: 12, barSpacing: 10, secondsVisible: false } });
        setTimeout(() => { try { chart.timeScale().fitContent(); } catch {} }, 0);
        requestAnimationFrame(() => { try { chart.timeScale().fitContent(); } catch {} });
        if (sortedCandleData.length > 1) {
          const from = sortedCandleData[0].time;
          const to = sortedCandleData[sortedCandleData.length - 1].time;
          console.log('Forcing visibleRange (candles):', { from, to });
          try { chart.timeScale().setVisibleRange({ from, to }); } catch {}
          try { chart.applyOptions({ timeScale: { visible: true } }); } catch {}
          const range = chart.timeScale().getVisibleRange();
          console.log('Visible range after candles set:', range);
          setVisibleRange({ from, to });
        }
        chart.timeScale().applyOptions({ tickMarkFormatter: formatTickLabel });
        
        // Fit chart content after rendering candlestick chart
        chart.timeScale().fitContent();
      }
      
      // Create markers for trades (buy/sell signals)
      if (trades && trades.length) {
        
        console.log('Raw trades data structure:', trades && trades.length ? trades[0] : 'No trades');
        
        // Analyze raw trade data first to understand structure
        if (trades && trades.length > 0) {
          console.log('Sample raw trade object keys:', Object.keys(trades[0]));
          console.log('Sample raw trade object:', trades[0]);
        }

        // Get ALL trades - treating every trade as a potential index signal
        // This is a fallback since we're unable to properly filter index vs option trades
        let indexTrades = [];
        
        // Only add BUY/SELL signals for index - check various patterns
        trades.forEach(trade => {
          // Use trade properties to determine if it's an index trade
          const action = trade.action || '';
          const isIndexTrade = 
            // Either explicitly marked as index
            (trade.is_index_trade) || 
            // Or has index symbol without option details
            (trade.symbol_info && 
             trade.symbol_info.index && 
             !trade.symbol_info.option_type && 
             !trade.symbol_info.strike) ||
            // Or is a BUY/SELL index signal
            (action.toLowerCase().includes('buy') || action.toLowerCase().includes('sell'));
          
          if (isIndexTrade) {
            console.log('Found index trade:', trade);
            indexTrades.push(trade);
          } else {
            console.log('Skipping non-index trade:', trade);
          }
        });
        
        // If we still don't have any index trades, use all trades as a fallback
        if (indexTrades.length === 0 && trades.length > 0) {
          console.log('No index trades found. Using all trades as fallback.');
          indexTrades = trades;
        }
        
        console.log(`Filtered ${trades.length - indexTrades.length} option trades. Remaining index trades: ${indexTrades.length}`);
        if (indexTrades.length > 0) {
          console.log('Sample index trade:', indexTrades[0]);
        }
        
        // Process filtered trades and create markers
        // Group trades by timestamp to avoid overlapping markers
        const tradesByTimestamp = {};
        // Collect per-trade logging rows for CSV export
        const logRows = [];
        
        console.log('Trade timestamp fields check:');
        if (indexTrades.length > 0) {
          const sampleTrade = indexTrades[0];
          console.log('- timestamp:', sampleTrade.timestamp ? new Date(sampleTrade.timestamp).toLocaleString() : 'N/A');
          console.log('- entry_time:', sampleTrade.entry_time ? new Date(sampleTrade.entry_time).toLocaleString() : 'N/A');
          console.log('- time:', sampleTrade.time ? new Date(sampleTrade.time).toLocaleString() : 'N/A');
          console.log('- date:', sampleTrade.date ? new Date(sampleTrade.date).toLocaleString() : 'N/A');
        }
        
        indexTrades.forEach((trade, index) => {
          console.log(`Processing trade ${index + 1}/${indexTrades.length}:`, trade);
          
          // Handle various timestamp formats in trades
          // Also apply IST offset to trade timestamps (UTC+5:30)
          let entryTime, exitTime;
          
          // For FractalStrategySimple or similar where we expect timestamp or similar fields
          if (trade.timestamp && typeof trade.timestamp === 'string') {
            console.log(`🔧 PROCESSING TRADE TIMESTAMP: "${trade.timestamp}"`);
            
            try {
              // CRITICAL: Parse trades EXACTLY like price data for consistency
              // Both come from backend as IST timestamps, both need identical parsing
              
              let normalizedTimestamp = trade.timestamp;
              if (trade.timestamp.includes(' ') && !trade.timestamp.includes('T')) {
                // Convert "2025-06-16 10:15:00" to "2025-06-16T10:15:00"
                normalizedTimestamp = trade.timestamp.replace(' ', 'T');
                console.log(`  Normalized: "${normalizedTimestamp}"`);
              }
              
              // SIMPLE FIX: Parse trade timestamps exactly like price data (FORCE IST)
              // Backend sends IST timestamps without TZ, append '+05:30' and parse
              if (normalizedTimestamp.includes('T') && !normalizedTimestamp.includes('+') && !normalizedTimestamp.includes('Z')) {
                const istString = `${normalizedTimestamp}+05:30`;
                const istDate = new Date(istString);
                entryTime = Math.floor(istDate.getTime() / 1000);
                
                console.log(`  🔧 TRADE (IST): ${normalizedTimestamp} -> ${istDate.toLocaleString()}`);
                console.log(`  🔧 Chart TS: ${entryTime}`);
              } else {
                const date = new Date(normalizedTimestamp);
                entryTime = Math.floor(date.getTime() / 1000);
              }
              
            } catch (error) {
              console.error(`❌ Error parsing trade timestamp "${trade.timestamp}":`, error);
              // Fallback
              entryTime = Math.floor(new Date(trade.timestamp).getTime() / 1000);
            }
          } else if (trade.entry_time) {
            // Apply same IST parsing logic without unused destructuring
            if (trade.entry_time.includes('T') && !trade.entry_time.includes('+') && !trade.entry_time.includes('Z')) {
              const istDate = new Date(`${trade.entry_time}+05:30`);
              entryTime = Math.floor(istDate.getTime() / 1000);
            } else {
              const utcMs = new Date(trade.entry_time).getTime();
              const istMs = utcMs + (5.5 * 60 * 60 * 1000);
              entryTime = Math.floor(istMs / 1000);
            }
          } else if (trade.time) {
            if (trade.time.includes && trade.time.includes('T') && !trade.time.includes('+') && !trade.time.includes('Z')) {
              const istDate = new Date(`${trade.time}+05:30`);
              entryTime = Math.floor(istDate.getTime() / 1000);
            } else {
              const utcMs = new Date(trade.time).getTime();
              const istMs = utcMs + (5.5 * 60 * 60 * 1000);
              entryTime = Math.floor(istMs / 1000);
            }
          } else if (trade.date) {
            if (trade.date.includes('T') && !trade.date.includes('+') && !trade.date.includes('Z')) {
              const istDate = new Date(`${trade.date}+05:30`);
              entryTime = Math.floor(istDate.getTime() / 1000);
            } else {
              const utcMs = new Date(trade.date).getTime();
              const istMs = utcMs + (5.5 * 60 * 60 * 1000);
              entryTime = Math.floor(istMs / 1000);
            }
          }
          
          // Try to infer timestamp from the result date or signal properties if available
          if (!entryTime && trade.signal_time) {
            const utcMs = new Date(trade.signal_time).getTime();
            const istMs = utcMs + (5.5 * 60 * 60 * 1000);
            entryTime = Math.floor(istMs / 1000);
          }
          
          console.log(`Trade ${index + 1} entry time (IST):`, entryTime ? new Date(entryTime * 1000).toLocaleString() : 'Not found');
          
          // Get exit time if available - apply same IST logic
          if (trade.exit_time) {
            if (trade.exit_time.includes('T') && !trade.exit_time.includes('+') && !trade.exit_time.includes('Z')) {
              const istDate = new Date(`${trade.exit_time}+05:30`);
              exitTime = Math.floor(istDate.getTime() / 1000);
            } else {
              const utcMs = new Date(trade.exit_time).getTime();
              const istMs = utcMs + (5.5 * 60 * 60 * 1000);
              exitTime = Math.floor(istMs / 1000);
            }
          } else if (trade.close_time) {
            if (trade.close_time.includes('T') && !trade.close_time.includes('+') && !trade.close_time.includes('Z')) {
              const istDate = new Date(`${trade.close_time}+05:30`);
              exitTime = Math.floor(istDate.getTime() / 1000);
            } else {
              const utcMs = new Date(trade.close_time).getTime();
              const istMs = utcMs + (5.5 * 60 * 60 * 1000);
              exitTime = Math.floor(istMs / 1000);
            }
          }
          
          // Determine if it's a buy or sell signal based on action
          let isBuy = true;
          if (trade.action && trade.action.toLowerCase().includes('sell')) {
            isBuy = false;
          } else if (trade.trade_type && trade.trade_type.toLowerCase().includes('sell')) {
            isBuy = false;
          }
          
          console.log(`🎯 TRADE ${index + 1} ACTION: ${trade.action} -> ${isBuy ? 'BUY' : 'SELL'} marker`);
          console.log(`   Trade type: ${trade.trade_type}`);
          console.log(`   Symbol: ${trade.symbol}`);
          console.log(`   Raw timestamp: ${trade.timestamp}`);
          
          // Skip if no valid timestamp
          if (!entryTime) {
            console.log(`❌ SKIPPING TRADE ${index + 1}: No entry time found`);
            return;
          }
          
          // Debug timestamp matching
          const tradeDate = new Date(entryTime * 1000);
          console.log(`🔍 MATCHING TRADE ${index + 1}: Looking for candle at ${tradeDate.toISOString()} (${tradeDate.toLocaleString()})`);
          
          // Instead of finding an exact match, find the nearest candle
          let closestCandle = null;
          let minTimeDiff = Infinity;
          
          // Find the closest candle by timestamp
          formattedData.forEach(candle => {
            // Calculate time difference between candle and trade
            const timeDiff = Math.abs(candle.time - entryTime);
            
            if (timeDiff < minTimeDiff) {
              minTimeDiff = timeDiff;
              closestCandle = candle;
            }
          });
          
          // Consider it a match only if within 10 minutes (tight tolerance)
          const candleMatch = minTimeDiff <= 10 * 60 ? closestCandle : null;
          
          if (candleMatch) {
            const matchDate = new Date(candleMatch.time * 1000);
            console.log(`✅ MATCH FOUND for trade ${index + 1}:`, matchDate.toISOString(), 'formatted as', matchDate.toLocaleString(), 
              'time difference (minutes):', Math.round(minTimeDiff / 60));
            console.log(`   Candle price: ${candleMatch.value}, Marker will be ${isBuy ? 'BUY (green)' : 'SELL (red)'}`);
            // Add to CSV log
            logRows.push({
              trade_index: index + 1,
              trade_action: isBuy ? 'BUY' : 'SELL',
              trade_raw_timestamp: trade.timestamp || trade.entry_time || trade.time || trade.date || 'N/A',
              trade_parsed_epoch: entryTime,
              trade_parsed_local: new Date(entryTime * 1000).toLocaleString(),
              candle_epoch: candleMatch.time,
              candle_local: matchDate.toLocaleString(),
              diff_minutes: Math.round(minTimeDiff / 60),
            });
          } else {
            console.log(`❌ NO MATCH for trade ${index + 1}: No candle found within 24 hours of ${tradeDate.toLocaleString()}`);
            // Add to CSV log as unmatched
            logRows.push({
              trade_index: index + 1,
              trade_action: isBuy ? 'BUY' : 'SELL',
              trade_raw_timestamp: trade.timestamp || trade.entry_time || trade.time || trade.date || 'N/A',
              trade_parsed_epoch: entryTime,
              trade_parsed_local: new Date(entryTime * 1000).toLocaleString(),
              candle_epoch: '',
              candle_local: '',
              diff_minutes: '',
            });
          }
          
          if (candleMatch) {
            // Create detailed trade description for chart marker
            const createTradeDescription = (trade) => {
              let description = `${isBuy ? 'BUY' : 'SELL'} ${trade.quantity || 1}`;

              if (trade.symbol_info?.index && trade.symbol_info?.strike) {
                const strike = trade.symbol_info.strike;
                const optionType = trade.symbol_info.option_type?.toUpperCase();
                description += ` ${trade.symbol_info.index} ${strike} ${optionType}`;
              }

              if (trade.price) {
                description += ` @ ₹${trade.price.toFixed(2)}`;
              }

              const exitTrue = Array.isArray(trade.exit_true) ? trade.exit_true.filter(Boolean) : [];
              if (exitTrue.length > 0) {
                description += ` [${exitTrue.join(' | ')}]`;
              } else if (!isBuy && trade.trade_type && trade.trade_type !== 'entry') {
                description += ` [${trade.trade_type}]`;
              }

              return description;
            };

            const detailedDescription = createTradeDescription(trade);
            console.log(`🏷️ MARKER TEXT: "${detailedDescription}"`);
            
            // Key for grouping trades by time and position
            const timeKey = `${candleMatch.time}_${isBuy ? 'buy' : 'sell'}`;
            
            if (!tradesByTimestamp[timeKey]) {
              tradesByTimestamp[timeKey] = {
                time: candleMatch.time,
                position: isBuy ? 'belowBar' : 'aboveBar',
                color: isBuy ? '#22c55e' : '#ef4444',
                shape: isBuy ? 'arrowUp' : 'arrowDown',
                text: detailedDescription, // Use detailed description
                size: 1.2,  // Slightly larger for better visibility
                count: 1
              };
            } else {
              // Increment count for duplicate signals
              tradesByTimestamp[timeKey].count++;
              // Only increase size for the first few duplicates
              if (tradesByTimestamp[timeKey].count <= 3) {
                tradesByTimestamp[timeKey].size += 0.2;
              }
              // Update text to show count with first trade details
              const baseText = tradesByTimestamp[timeKey].text.split(' (+')[0]; // Remove existing count
              tradesByTimestamp[timeKey].text = `${baseText} (+${tradesByTimestamp[timeKey].count - 1} more)`;
            }
          }
          
          // If we have an exit time, add a closing marker
          if (exitTime) {
            const exitCandle = formattedData.find(candle => 
              Math.abs(candle.time - exitTime) < 24 * 60 * 60); // Within 1 day
              
            if (exitCandle) {
              const closeKey = `${exitCandle.time}_${isBuy ? 'sell' : 'buy'}`;
              
              if (!tradesByTimestamp[closeKey]) {
                tradesByTimestamp[closeKey] = {
                  time: exitCandle.time,
                  position: isBuy ? 'aboveBar' : 'belowBar', // Opposite position for exit
                  color: isBuy ? '#FF5252' : '#2196F3', // Opposite color for exit
                  shape: isBuy ? 'arrowDown' : 'arrowUp', // Opposite shape for exit
                  text: isBuy ? 'SELL' : 'BUY',
                  size: 1,
                  count: 1
                };
              } else {
                // Increment count for duplicate exit signals
                tradesByTimestamp[closeKey].count++;
                // Only increase size for the first few duplicates
                if (tradesByTimestamp[closeKey].count <= 3) {
                  tradesByTimestamp[closeKey].size += 0.2;
                }
                // Update text to show count
                tradesByTimestamp[closeKey].text = `${isBuy ? 'SELL' : 'BUY'} (${tradesByTimestamp[closeKey].count})`;
              }
            }
          }
        });
        
        // Convert grouped trades to markers
        const consolidatedMarkers = Object.values(tradesByTimestamp);
        console.log(`\n📊 CHART MARKERS SUMMARY:`);
        console.log(`   Total markers to add: ${consolidatedMarkers.length}`);
        console.log(`   Processed ${indexTrades.length} trades`);
        
        if (consolidatedMarkers.length > 0) {
          console.log("   First 3 markers:");
          consolidatedMarkers.slice(0, 3).forEach((marker, idx) => {
            const date = new Date(marker.time * 1000);
            console.log(`     ${idx + 1}. ${marker.text} at ${date.toLocaleString()} (${marker.position})`);
          });
        } else {
          console.log("   ❌ NO MARKERS CREATED - Check trade processing above!");
        }
        
        if (consolidatedMarkers.length > 0 && activeSeries) {
          console.log('Adding markers to chart');
          console.log(`Adding markers to ${chartType === 'premium' ? 'premium' : 'candlestick'} series`);
          // Add markers to the active series
          activeSeries.setMarkers(consolidatedMarkers);
        } else {
          console.log('No markers to add to chart or no active series');
        }

        // Generate CSV log and attach download link above chart
        try {
          const headers = [
            'trade_index','trade_action','trade_raw_timestamp','trade_parsed_epoch','trade_parsed_local',
            'candle_epoch','candle_local','diff_minutes'
          ];
          const csv = [headers.join(',')].concat(
            logRows.map(r => headers.map(h => (r[h] !== undefined ? String(r[h]).replace(/,/g, ';') : '')).join(','))
          ).join('\n');
          const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob);
          const linkId = 'trade-plot-log-link';
          let linkEl = document.getElementById(linkId);
          if (!linkEl) {
            linkEl = document.createElement('a');
            linkEl.id = linkId;
            linkEl.style.display = 'inline-block';
            linkEl.style.margin = '8px 0';
            linkEl.style.fontSize = '12px';
            linkEl.style.color = '#2563eb';
            linkEl.textContent = 'Download trade vs plotted time log (CSV)';
            // Insert the link just above the chart container
            if (chartContainerRef.current && chartContainerRef.current.parentElement) {
              chartContainerRef.current.parentElement.insertBefore(linkEl, chartContainerRef.current);
            }
          }
          const filename = `trade_plot_log_${Date.now()}.csv`;
          linkEl.href = url;
          linkEl.download = filename;
        } catch (e) {
          console.warn('Failed to create CSV log:', e);
        }
      }
      
      // Fit content to container
      chart.timeScale().fitContent();

      // Crosshair time label updater (uses overlayEl created above)
      chart.subscribeCrosshairMove((param) => {
        const ts = param && param.time
          ? (typeof param.time === 'number' ? param.time : param.time.seconds)
          : null;
        if (!ts) return;
        const d = new Date(ts * 1000);
        const YY = d.getFullYear();
        const MM = (d.getMonth()+1).toString().padStart(2,'0');
        const DD = d.getDate().toString().padStart(2,'0');
        const hh = d.getHours().toString().padStart(2,'0');
        const mm = d.getMinutes().toString().padStart(2,'0');
        const el = document.getElementById('chart-time-overlay');
        if (el) el.textContent = `Crosshair: ${YY}-${MM}-${DD} ${hh}:${mm}`;

        const valuesEl = document.getElementById('chart-values-overlay');
        if (valuesEl) {
          const meta = metaByTime.get(ts);
          const parts = [];
          if (meta) {
            if (meta.open != null && meta.high != null && meta.low != null && meta.close != null) {
              parts.push(`O: ${meta.open.toFixed(2)}`);
              parts.push(`H: ${meta.high.toFixed(2)}`);
              parts.push(`L: ${meta.low.toFixed(2)}`);
              parts.push(`C: ${meta.close.toFixed(2)}`);
            }
            if (meta.volume != null) parts.push(`V: ${Math.round(meta.volume)}`);
            if (meta.oi != null) parts.push(`OI: ${Math.round(meta.oi)}`);
            if (meta.vwap != null) parts.push(`VWAP: ${meta.vwap.toFixed(2)}`);
            if (meta.lr != null) parts.push(`LR: ${meta.lr.toFixed(2)}`);
            if (meta.prev_sl_line != null) parts.push(`PrevSL: ${meta.prev_sl_line.toFixed(2)}`);
            if (meta.prev_vwap != null) parts.push(`PrevVWAP: ${meta.prev_vwap.toFixed(2)}`);
            if (meta.prev_down_line != null) parts.push(`PrevDown: ${meta.prev_down_line.toFixed(2)}`);
            if (meta.sl_calc_cur != null) parts.push(`SLcalcCur: ${meta.sl_calc_cur.toFixed(2)}`);
            if (meta.sl_calc_prev != null) parts.push(`SLcalcPrev: ${meta.sl_calc_prev.toFixed(2)}`);
            if (meta.sl_line != null) parts.push(`SL: ${meta.sl_line.toFixed(2)}`);
            if (meta.prev_high != null) parts.push(`Prev16High: ${meta.prev_high.toFixed(2)}`);
            if (meta.down_line != null) parts.push(`Down: ${meta.down_line.toFixed(2)}`);
            if (meta.sd != null) parts.push(`SD: ${meta.sd.toFixed(2)}`);
            if (meta.mode != null) parts.push(`Mode: ${meta.mode}`);
          }
          valuesEl.textContent = parts.join(' | ');
        }
      });

    } catch (error) {
      console.error('StableChart Error:', error);
    }
    
    // Handle resize
    const handleResize = () => {
      if (!chart || !chartContainerRef.current) return;
      chart.applyOptions({ 
        width: chartContainerRef.current.clientWidth,
        height: chartContainerRef.current.clientHeight || 500,
      });
    };
    
    window.addEventListener('resize', handleResize);

    // Force a relayout shortly after mount to ensure axis labels appear
    setTimeout(() => {
      try {
        if (chart && chartContainerRef.current) {
          chart.applyOptions({
            width: chartContainerRef.current.clientWidth,
            height: chartContainerRef.current.clientHeight || 500,
          });
          chart.timeScale().fitContent();
        }
      } catch (err) {
        console.warn('StableChart relayout failed:', err);
      }
    }, 50);

    // Cleanup on unmount
    return () => {
      window.removeEventListener('resize', handleResize);
      try {
        chart.timeScale().unsubscribeVisibleTimeRangeChange(handleVisibleRangeChange);
      } catch (err) {
        console.warn('Failed to unsubscribe visible range listener:', err);
      }
      try {
        chart.remove();
      } catch (err) {
        console.warn('Failed to remove chart:', err);
      }
    };
  }, [data, trades, chartType, showSupertrend, handleVisibleRangeChange]);

  const axisLabels = useMemo(() => {
    console.log('🏷️ AXIS LABELS COMPUTATION - visibleRange:', visibleRange);
    
    if (!visibleRange || visibleRange.from == null || visibleRange.to == null) {
      console.log('🏷️ No visible range, returning empty labels');
      return [];
    }

    const from = visibleRange.from;
    const to = visibleRange.to;
    console.log('🏷️ Creating labels from', from, 'to', to);

    if (from === to) {
      const singleLabel = formatTickLabel(from);
      console.log('🏷️ Single point label:', singleLabel);
      return [singleLabel];
    }

    const stepCount = 4; // Gives 5 labels including endpoints
    const labels = [];
    const span = to - from;

    if (span <= 0) {
      console.log('🏷️ Invalid span, returning single label');
      return [formatTickLabel(from)];
    }

    for (let i = 0; i <= stepCount; i += 1) {
      const point = from + (span * i) / stepCount;
      const label = formatTickLabel(point);
      labels.push(label);
      console.log(`🏷️ Label ${i}:`, label);
    }

    console.log('🏷️ Final axis labels:', labels);
    return labels;
  }, [visibleRange]);

  return (
    <div className="w-full mt-4" style={{ paddingBottom: '40px', overflow: 'visible', position: 'relative' }}>
      {/* Axis font CSS to guarantee label area */}
      <style>{`
        .tv-lightweight-charts *, .tv-lightweight-charts { font-size: 12px !important; }
      `}</style>
      <div ref={chartContainerRef} className="w-full" style={{ height: '500px', overflow: 'visible' }} />
      
      {/* Debug overlay - positioned INSIDE the chart area at the bottom */}
      <div 
        style={{ 
          position: 'absolute',
          bottom: '50px', // Move it up from the very bottom
          left: '10px',
          right: '10px',
          height: '30px',
          backgroundColor: 'rgba(255, 255, 0, 0.95)', 
          padding: '4px 12px',
          borderRadius: '6px',
          zIndex: 9999,
          color: '#000',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: '12px',
          fontWeight: 'bold',
          border: '2px solid #ff0000',
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
        }}
      >
        {axisLabels.length > 0 ? (
          axisLabels.map((label, idx) => (
            <span key={`${label}-${idx}`} style={{ whiteSpace: 'nowrap' }}>{label}</span>
          ))
        ) : (
          <span style={{ color: 'red' }}>NO LABELS - Check Console</span>
        )}
      </div>
      
      {/* Also add a simple test div to make sure rendering works */}
      <div style={{ 
        position: 'absolute', 
        top: '10px', 
        right: '10px', 
        backgroundColor: 'red', 
        color: 'white', 
        padding: '5px',
        zIndex: 9999,
        fontSize: '12px'
      }}>
        OVERLAY TEST - {axisLabels.length} labels
      </div>
    </div>
  );
};

export default StableChart;
